import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ListingCard } from "@/components/ListingCard";
import { CategoryGrid } from "@/components/home/CategoryGrid";
import { PeteChat } from "@/components/home/PeteChat";
import { HomeSeoSection } from "@/components/home/HomeSeoSection";
import { useSeo } from "@/hooks/useSeo";
import { categoryPath } from "@/lib/categories";
import api from "@/lib/api";

export default function Home() {
  const navigate = useNavigate();
  useSeo({
    title: "DealShed, Buy & Sell in Ireland: Motors, Jobs, Property, Farming & More",
    description: "Ireland's friendly classifieds marketplace. Buy and sell motors, machinery, furniture, fashion and find local jobs. Your first ad is free, then just €5.",
    canonical: `${window.location.origin}/`,
  });
  const [q, setQ] = useState("");
  const [county, setCounty] = useState("all");
  const [counties, setCounties] = useState([]);
  const [premium, setPremium] = useState([]);
  const [recent, setRecent] = useState([]);

  const [mode, setMode] = useState("browse"); // browse | pete
  const [peteInitialQuery, setPeteInitialQuery] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const ask = searchParams.get("ask_pete");
    if (ask) {
      setMode("pete");
      setPeteInitialQuery(ask);
      // Clear the param so a refresh doesn't re-trigger the auto-send.
      searchParams.delete("ask_pete");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCounties(r.data.counties));
    api.get("/listings", { params: { premium: true, limit: 8 } }).then((r) => setPremium(r.data));
    api.get("/listings", { params: { sort: "newest", limit: 8 } }).then((r) => setRecent(r.data));
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (county !== "all") p.set("county", county);
    navigate(`/search?${p.toString()}`);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero — Pete portrait + hook */}
      <section className="bg-gradient-to-b from-emerald-50 via-slate-50 to-white border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
          {/* Left: hook + toggle + browse */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Forget the search bar.<br /><span className="text-emerald-600">Trade with Pete.</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto lg:mx-0">Ireland's friendliest marketplace. Just tell Pete what you're after and he'll dig out the best deals, or browse the ads yourself.</p>

            {/* Mode toggle */}
            <div className="mt-7 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="search-mode-toggle">
              <button data-testid="mode-browse" onClick={() => setMode("browse")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${mode === "browse" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Browse</button>
              <button data-testid="mode-pete" onClick={() => setMode("pete")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${mode === "pete" ? "bg-emerald-600 text-white shadow-sm" : "text-slate-500"}`}>
                <MessageCircle size={14} /> Chat with Pete
              </button>
            </div>

            {mode === "browse" ? (
              <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm p-2 flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto lg:mx-0">
                <div className="flex-1 flex items-center gap-2 px-3 border border-slate-200 sm:border-0 rounded-md">
                  <Search size={18} className="text-slate-400" />
                  <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    placeholder="What are you looking for?" className="w-full py-2.5 outline-none text-slate-900" />
                </div>
                <div className="sm:w-44">
                  <Select value={county} onValueChange={setCounty}>
                    <SelectTrigger data-testid="hero-county-select" className="border-slate-200"><MapPin size={16} className="text-slate-400 mr-1" /><SelectValue placeholder="All Ireland" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">All Ireland</SelectItem>
                      {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="hero-search-button" onClick={doSearch} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">Search</Button>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500 hidden lg:block">Pete's ready when you are, say hello over on the right.</p>
            )}
          </div>

          {/* Right: Pete portrait (click to chat) OR the live chat */}
          <div className="order-1 lg:order-2">
            {mode === "pete" ? (
              <div>
                <PeteChat initialQuery={peteInitialQuery} />
                <button data-testid="pete-browse-link" onClick={() => setMode("browse")}
                  className="text-xs text-slate-400 mt-3 hover:text-emerald-600 transition-colors block mx-auto text-center w-full">Prefer the classic search? Browse all ads yourself</button>
              </div>
            ) : (
              <button data-testid="pete-hero-portrait" onClick={() => setMode("pete")}
                className="group relative mx-auto block w-full max-w-md focus:outline-none">
                {/* Speech bubble */}
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 lg:left-4 lg:translate-x-0 z-20 bg-white border border-emerald-200 shadow-md rounded-2xl rounded-bl-sm px-4 py-2 text-sm font-medium text-slate-800 whitespace-nowrap">
                  Awright! Tap me, let's find you a deal
                </span>
                {/* Portrait */}
                <span className="relative block rounded-[2rem] overflow-hidden ring-4 ring-white shadow-xl bg-emerald-100 aspect-square mt-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">
                  <img src="/pete-avatar.png" alt="Pete, your friendly DealShed market trader" data-testid="pete-hero-face"
                    className="w-full h-full object-cover object-top" />
                  {/* €5 starburst overlay */}
                  <span className="absolute bottom-3 right-3" data-testid="price-starburst-badge">
                    <span className="relative block">
                      <span className="price-starburst w-24 h-24 sm:w-28 sm:h-28 grid place-items-center text-white text-center select-none" aria-hidden="true">
                        <span className="leading-none">
                          <span className="block font-heading font-extrabold text-2xl sm:text-3xl">€5</span>
                          <span className="block font-heading font-bold uppercase tracking-wide text-[10px] sm:text-xs mt-0.5">per ad</span>
                        </span>
                      </span>
                      <span data-testid="first-free-ribbon" className="absolute -top-1 -right-2 rotate-12 bg-amber-400 text-slate-900 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full shadow-md border border-amber-500">1st free</span>
                    </span>
                  </span>
                </span>
                {/* CTA pill */}
                <span className="mt-4 inline-flex items-center gap-2 bg-emerald-600 group-hover:bg-emerald-700 text-white font-semibold rounded-full px-5 py-2.5 shadow-sm transition-colors">
                  <MessageCircle size={16} /> Start chatting with Pete
                </span>
              </button>
            )}
          </div>
        </div>
      </section>


      <CategoryGrid onSelect={(name) => navigate(categoryPath(name))} />

      {/* Premium / Spotlight */}
      {premium.length > 0 && (
        <section className="bg-amber-50/50 border-y border-amber-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">⭐ Spotlight listings</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {premium.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </div>
        </section>
      )}

      {/* Recent */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">Latest ads</h2>
          <button data-testid="view-all-listings" onClick={() => navigate("/search")} className="text-emerald-600 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">View all <ArrowRight size={16} /></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {recent.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      <HomeSeoSection />
      <Footer />
    </div>
  );
}
